import io from 'socket.io-client';

import {
  getSessionSnapshot,
  isServiceUnavailable,
  refreshUnlessRenewed,
} from 'services/session';

const uri = process.env.REACT_APP_SOCKET_ENDPOINT || '';
const RECOVERY_RETRY_MS = 5 * 1000;
const RECOVERY_RETRY_MAX_MS = 60 * 1000;

// A autenticação é feita pelo cookie httpOnly enviado no handshake
const socket = io(uri, {
  transports: ['websocket'],
  autoConnect: false,
  withCredentials: true,
  auth: { clientType: 'WEB' },
});

let refreshedAfterError = false;
let sessionSnapshot: string | undefined;
let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
let recoveryAttempt = 0;

// Sessão usada em cada handshake, para não renovar de novo se outra aba já renovou
socket.io.on('reconnect_attempt', () => {
  sessionSnapshot = getSessionSnapshot();
});

socket.on('connect', () => {
  refreshedAfterError = false;
  recoveryAttempt = 0;
});

// Backoff exponencial (5s, 10s... até 60s) com jitter de 20%
function getRecoveryDelay() {
  const base = Math.min(
    RECOVERY_RETRY_MS * 2 ** recoveryAttempt,
    RECOVERY_RETRY_MAX_MS,
  );
  recoveryAttempt += 1;

  return Math.round(base * (0.8 + Math.random() * 0.4));
}

async function recoverConnection() {
  try {
    await refreshUnlessRenewed(sessionSnapshot);
  } catch (error) {
    // Core instável: tenta de novo, senão o socket ficaria parado até recarregar a página
    if (isServiceUnavailable(error)) {
      recoveryTimer = setTimeout(recoverConnection, getRecoveryDelay());
    }

    // Sem sessão: a próxima requisição à API (401) redireciona ao login
    return;
  }

  sessionSnapshot = getSessionSnapshot();
  socket.connect();
}

// Handshake recusado (ex.: access token vencido): o socket.io não tenta de novo sozinho,
// então renova a sessão (repetindo enquanto o core estiver instável) e reconecta uma vez
socket.on('connect_error', () => {
  if (socket.active || refreshedAfterError) return;

  refreshedAfterError = true;
  recoverConnection();
});

export function connect(): void {
  clearTimeout(recoveryTimer);

  if (!socket.connected) {
    sessionSnapshot = getSessionSnapshot();
    socket.connect();
  }
}

export function reset(): void {
  clearTimeout(recoveryTimer);
  socket.disconnect();
}

export default socket;
